import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Checkbox } from '../../ui/checkbox'
import { supabase } from '../../../lib/supabase'
import { toast } from 'sonner'
import { Link, useNavigate } from 'react-router-dom'
import { trackEvent, EventCategory } from '../../../lib/analytics'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs'
import { SITE_CONFIG } from '../../../lib/constants'
import { debug } from '../../../lib/debug'
import { Loader2, Mail } from 'lucide-react'

export function SurveyAuth() {
  const [isLoading, setIsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('login')
  const navigate = useNavigate()

  // Login state
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginError, setLoginError] = useState('')

  // Signup state
  const [signupEmail, setSignupEmail] = useState('')
  const [signupPassword, setSignupPassword] = useState('')
  const [signupPin, setSignupPin] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [signupError, setSignupError] = useState('')
  const [showConfirmation, setShowConfirmation] = useState(false)

  // Reset password state
  const [resetEmail, setResetEmail] = useState('')
  const [resetSent, setResetSent] = useState(false)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError('')
    setIsLoading(true)
  
    try {
      if (!loginEmail || !loginPassword) {
        throw new Error('Please enter both email and password')
      }
  
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginEmail.trim(),
        password: loginPassword
      })
  
      if (error) {
        // Special handling for email not confirmed error
        if (error.message === 'Email not confirmed') {
          setLoginError('Please confirm your email address to sign in')
          return (
            <div className="mt-2 flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  const { error } = await supabase.auth.resend({
                    type: 'signup',
                    email: loginEmail.trim(),
                  })
                  if (!error) {
                    toast.success('Confirmation email resent')
                  } else {
                    toast.error('Failed to resend confirmation email')
                  }
                }}
                className="h-auto p-0 text-sm text-primary hover:text-primary/80"
              >
                Resend confirmation email
              </Button>
            </div>
          )
        }
        
        if (error.message === 'Invalid login credentials') {
          throw new Error('Invalid email or password')
        }
        throw error
      }
  
      if (!data.user) {
        throw new Error('Login failed - please try again')
      }
  
      if (!data.user.email_confirmed_at) {
        setLoginError('Please confirm your email address to sign in')
        return
      }
  
      trackEvent({
        action: 'login',
        category: EventCategory.Auth,
        label: 'survey_creator_login'
      })
  
      toast.success('Successfully logged in')
      navigate('/survey/manage')
    } catch (error) {
      console.error('Login error:', error)
      setLoginError(error instanceof Error ? error.message : 'Failed to log in')
      toast.error(error instanceof Error ? error.message : 'Failed to log in')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setSignupError('')
    
    if (!acceptedTerms) {
      setSignupError('Please accept the terms and conditions to continue')
      return
    }

    setIsLoading(true)
    let pinId: string | null = null

    try {
      if (!signupEmail || !signupPassword || !signupPin) {
        throw new Error('Please fill in all fields')
      }

      if (signupPassword.length < 8) {
        throw new Error('Password must be at least 8 characters long')
      }

      // First verify the signup PIN
      const { data: pinData } = await supabase
        .rpc('is_pin_valid', { pin_value: signupPin })

      if (!pinData) {
        throw new Error('Invalid or expired signup PIN')
      }

      pinId = pinData

      debug('SIGNUP', 'Valid PIN found:', pinId)

      // Create the user account
      const { data: { user }, error: signupError } = await supabase.auth.signUp({
        email: signupEmail.trim(),
        password: signupPassword,
        options: {
          data: {
            accepted_terms: true,
            accepted_terms_at: new Date().toISOString(),
          }
        }
      })

      if (signupError) throw signupError
      if (!user) throw new Error('Failed to create user account')

      debug('SIGNUP', 'User created:', user.id)

      // Mark the PIN as used
      const { data: markResult, error: markError } = await supabase
        .rpc('mark_pin_as_used', {
          pin_id: pinId,
          user_id: user.id
        })

      if (markError || !markResult) {
        debug('SIGNUP', 'Error marking PIN as used:', markError)
      } else {
        debug('SIGNUP', 'PIN marked as used:', pinId)
      }

      trackEvent({
        action: 'signup',
        category: EventCategory.Auth,
        label: 'survey_creator_signup'
      })

      if (SITE_CONFIG.SKIP_EMAIL_CONFIRMATION) {
        // Skip confirmation and redirect directly to survey management
        navigate('/survey/manage')
        toast.success('Account created successfully')
      } else {
        // Show confirmation screen
        setShowConfirmation(true)
        toast.success('Account created! Please check your email to confirm your address.')
      }
    } catch (error: any) {
      console.error('Error signing up:', error)
      
      const errorMessage = error.message === 'Invalid login credentials' 
        ? 'Invalid email or password'
        : error.message

      setSignupError(errorMessage)
      toast.error(errorMessage)

      // If PIN was found but signup failed, we should unlock it
      if (pinId) {
        try {
          const { error } = await supabase
            .from('signup_pins')
            .update({ used_by: null, used_at: null })
            .eq('id', pinId);
            
          if (error) {
            debug('SIGNUP', 'Error unlocking PIN:', error);
          } else {
            debug('SIGNUP', 'PIN unlocked after failed signup:', pinId);
          }
        } catch (unlockError) {
          debug('SIGNUP', 'Error unlocking PIN:', unlockError);
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        redirectTo: `${window.location.origin}/auth/update-password`
      })

      if (error) throw error

      setResetSent(true)
      toast.success('Password reset instructions sent to your email')

      trackEvent({
        action: 'request',
        category: EventCategory.Auth,
        label: 'password_reset_requested'
      })
    } catch (error) {
      console.error('Error requesting password reset:', error)
      toast.error('Failed to send reset instructions. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (showConfirmation) {
    return (
      <div className="mt-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto"
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 space-y-2">
                  <h2 className="text-lg font-semibold">Check Your Email</h2>
                  <p className="text-muted-foreground">
                    We've sent a confirmation link to <span className="font-medium">{signupEmail}</span>. 
                    Please check your email and click the link to verify your account.
                  </p>
                  <Button 
                    variant="outline"
                    onClick={async () => {
                      const { error } = await supabase.auth.resend({
                        type: 'signup',
                        email: signupEmail,
                      })
                      if (!error) {
                        toast.success('Confirmation email resent')
                      }
                    }}
                  >
                    Resend Confirmation Email
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowConfirmation(false)
                      setActiveTab('login')
                    }}
                    className="w-full"
                  >
                    Back to Sign In
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="mt-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md mx-auto"
      >
        <Card>
          <CardHeader>
            <CardTitle>Authentication</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Create Account</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                {!resetSent ? (
                  <form onSubmit={handleLogin} className="space-y-6">
                    <div className="space-y-2">
                      <label htmlFor="loginEmail" className="text-sm font-medium">
                        Email
                      </label>
                      <Input
                        id="loginEmail"
                        type="email"
                        value={loginEmail}
                        onChange={(e) => {
                          setLoginEmail(e.target.value)
                          setLoginError('')
                        }}
                        required
                        disabled={isLoading}
                        placeholder="Enter your email"
                        autoComplete="email"
                        className={loginError ? 'border-destructive' : ''}
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="loginPassword" className="text-sm font-medium">
                        Password
                      </label>
                      <Input
                        id="loginPassword"
                        type="password"
                        value={loginPassword}
                        onChange={(e) => {
                          setLoginPassword(e.target.value)
                          setLoginError('')
                        }}
                        required
                        disabled={isLoading}
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        className={loginError ? 'border-destructive' : ''}
                      />
                    </div>

                    {loginError && (
                      <div className="space-y-2">
                        <p className="text-sm text-destructive">{loginError}</p>
                        {loginError.includes('confirm your email') && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              const { error } = await supabase.auth.resend({
                                type: 'signup',
                                email: loginEmail.trim(),
                              })
                              if (!error) {
                                toast.success('Confirmation email resent')
                              } else {
                                toast.error('Failed to resend confirmation email')
                              }
                            }}
                            className="h-auto p-0 text-sm text-primary hover:text-primary/80"
                          >
                            Resend confirmation email
                          </Button>
                        )}
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={isLoading || !loginEmail || !loginPassword}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Signing in...
                          </>
                        ) : (
                          'Sign In'
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setResetSent(true)}
                        className="text-sm text-muted-foreground hover:text-foreground"
                      >
                        Forgot password?
                      </Button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleResetPassword} className="space-y-6">
                    <div className="space-y-2">
                      <label htmlFor="resetEmail" className="text-sm font-medium">
                        Email
                      </label>
                      <Input
                        id="resetEmail"
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                        disabled={isLoading}
                        placeholder="Enter your email"
                        autoComplete="email"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={isLoading || !resetEmail}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Send Reset Instructions'
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setResetSent(false)}
                        className="text-sm text-muted-foreground hover:text-foreground"
                      >
                        Back to Sign In
                      </Button>
                    </div>
                  </form>
                )}
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-6">
                  <div className="space-y-2">
                    <label htmlFor="signupPin" className="text-sm font-medium">
                      Signup PIN
                    </label>
                    <Input
                      id="signupPin"
                      type="text"
                      value={signupPin}
                      onChange={(e) => {
                        setSignupPin(e.target.value)
                        setSignupError('')
                      }}
                      required
                      disabled={isLoading}
                      placeholder="Enter 6-digit signup PIN"
                      maxLength={6}
                      pattern="[0-9]{6}"
                      className={`text-center ${signupError ? 'border-destructive' : ''}`}
                    />
                    <p className="text-xs text-muted-foreground">
                      Please contact us at <a href="mailto:info@syllepsis.io" className="underline">info@syllepsis.io</a> for a signup PIN
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="signupEmail" className="text-sm font-medium">
                      Email
                    </label>
                    <Input
                      id="signupEmail"
                      type="email"
                      value={signupEmail}
                      onChange={(e) => {
                        setSignupEmail(e.target.value)
                        setSignupError('')
                      }}
                      required
                      disabled={isLoading}
                      placeholder="Enter your email"
                      autoComplete="email"
                      className={signupError ? 'border-destructive' : ''}
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="signupPassword" className="text-sm font-medium">
                      Password
                    </label>
                    <Input
                      id="signupPassword"
                      type="password"
                      value={signupPassword}
                      onChange={(e) => {
                        setSignupPassword(e.target.value)
                        setSignupError('')
                      }}
                      required
                      disabled={isLoading}
                      placeholder="Create a password"
                      minLength={8}
                      autoComplete="new-password"
                      className={signupError ? 'border-destructive' : ''}
                    />
                    <p className="text-xs text-muted-foreground">
                      Password must be at least 8 characters long
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="terms"
                        checked={acceptedTerms}
                        onCheckedChange={(checked) => {
                          setAcceptedTerms(checked as boolean)
                          setSignupError('')
                        }}
                        disabled={isLoading}
                      />
                      <div className="grid gap-1.5 leading-none">
                        <label
                          htmlFor="terms"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Accept terms and conditions
                        </label>
                        <p className="text-sm text-muted-foreground">
                          By creating an account, you agree to our{' '}
                          <Link to="/terms" className="underline">
                            Terms of Service
                          </Link>{' '}
                          and{' '}
                          <Link to="/privacy" className="underline">
                            Privacy Policy
                          </Link>
                        </p>
                      </div>
                    </div>
                  </div>

                  {signupError && (
                    <p className="text-sm text-destructive">{signupError}</p>
                  )}

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isLoading || !acceptedTerms || !signupEmail || !signupPassword || !signupPin}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}